import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    ClipboardList,
    Plus,
    Truck,
    User,
    MapPin,
    Clock,
    FileDown,
    Edit,
    Trash2,
    Flag,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
    chauffeur_id: '', chauffeur_name: '', vehicle_id: '', vehicle_name: '',
    objet: '', point_depart: '', destinations: '', date_depart: '', date_retour_prevu: '',
    autorite: '', observations: ''
};

const fmtDt = (v) => v ? v.slice(0, 16).replace('T', ' ') : '-';

const OrdresMissionPage = () => {
    const { token, canManageOrdresMission, user } = useAuth();
    const [ordres, setOrdres] = useState([]);
    const [chauffeurs, setChauffeurs] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);

    const headers = { Authorization: `Bearer ${token}` };

    const fetchData = useCallback(async () => {
        try {
            const reqs = [axios.get(`${API}/ordres-mission`, { headers })];
            if (canManageOrdresMission) {
                reqs.push(axios.get(`${API}/ordres-mission/options`, { headers }).catch(() => ({ data: { chauffeurs: [], vehicles: [] } })));
            }
            const res = await Promise.all(reqs);
            setOrdres(res[0].data);
            if (canManageOrdresMission && res[1]) {
                setChauffeurs(res[1].data.chauffeurs || []);
                setVehicles(res[1].data.vehicles || []);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, canManageOrdresMission]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const openCreate = () => { setEditing(null); setForm(emptyForm); setShowForm(true); };

    const openEdit = (o) => {
        setEditing(o);
        setForm({
            chauffeur_id: o.chauffeur_id || '', chauffeur_name: o.chauffeur_name || '',
            vehicle_id: o.vehicle_id || '', vehicle_name: o.vehicle_name || '',
            objet: o.objet || '', point_depart: o.point_depart || '', destinations: o.destinations || '',
            date_depart: o.date_depart || '', date_retour_prevu: o.date_retour_prevu || '',
            autorite: o.autorite || '', observations: o.observations || ''
        });
        setShowForm(true);
    };

    const handleSubmit = async () => {
        if (!form.objet.trim()) { toast.error('L\'objet de la mission est requis'); return; }
        try {
            if (editing) {
                await axios.put(`${API}/ordres-mission/${editing.id}`, form, { headers });
                toast.success('Ordre de mission mis a jour');
            } else {
                await axios.post(`${API}/ordres-mission`, form, { headers });
                toast.success('Ordre de mission cree');
            }
            setShowForm(false);
            setForm(emptyForm);
            setEditing(null);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Supprimer cet ordre de mission ?')) return;
        try {
            await axios.delete(`${API}/ordres-mission/${id}`, { headers });
            toast.success('Ordre de mission supprime');
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    if (loading) {
        return <div className="flex items-center justify-center h-full"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div></div>;
    }

    return (
        <div className="space-y-6" data-testid="ordres-mission-page">
            <div className="page-header">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <h1 className="page-title">Ordres de Mission</h1>
                        <p className="page-subtitle">
                            {canManageOrdresMission ? 'Document officiel missionnant un chauffeur' : 'Vos ordres de mission'}
                        </p>
                    </div>
                    {canManageOrdresMission && (
                        <Button onClick={openCreate} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="create-ordre-btn">
                            <Plus className="w-4 h-4 mr-2" /> Nouvel ordre de mission
                        </Button>
                    )}
                </div>
            </div>

            {ordres.length === 0 ? (
                <Card className="content-card">
                    <CardContent className="p-12 text-center">
                        <ClipboardList className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                        <p className="text-zinc-400">Aucun ordre de mission</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {ordres.map(o => (
                        <Card key={o.id} className="content-card" data-testid={`ordre-card-${o.id}`}>
                            <CardContent className="p-5 space-y-3">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 font-mono">{o.numero}</Badge>
                                            <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">{(o.status || 'emis').toUpperCase()}</Badge>
                                        </div>
                                        <h3 className="text-lg font-semibold text-white mt-2 flex items-center gap-2"><Flag className="w-4 h-4 text-amber-400 flex-shrink-0" /> {o.objet}</h3>
                                    </div>
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        <a href={`${API}/export/ordre-mission/${o.id}?token=${token}`} target="_blank" rel="noopener noreferrer"
                                            className="inline-flex items-center justify-center h-8 w-8 rounded-md text-cyan-400 hover:bg-cyan-500/10" title="Exporter en PDF" data-testid={`ordre-pdf-${o.id}`}>
                                            <FileDown className="w-4 h-4" />
                                        </a>
                                        {canManageOrdresMission && (
                                            <>
                                                <Button variant="ghost" size="sm" onClick={() => openEdit(o)} className="h-8 w-8 p-0 text-blue-400 hover:text-blue-300" data-testid={`edit-ordre-${o.id}`}>
                                                    <Edit className="w-4 h-4" />
                                                </Button>
                                                <Button variant="ghost" size="sm" onClick={() => handleDelete(o.id)} className="h-8 w-8 p-0 text-red-400 hover:text-red-300" data-testid={`delete-ordre-${o.id}`}>
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                                    <div className="flex items-center gap-2 text-zinc-300"><User className="w-4 h-4 text-zinc-500" /> {o.chauffeur_name || '-'}</div>
                                    <div className="flex items-center gap-2 text-zinc-300"><Truck className="w-4 h-4 text-zinc-500" /> {o.vehicle_name || '-'}</div>
                                    <div className="flex items-center gap-2 text-zinc-300"><MapPin className="w-4 h-4 text-zinc-500" /> {o.point_depart || '-'}</div>
                                    <div className="flex items-center gap-2 text-zinc-300"><MapPin className="w-4 h-4 text-emerald-500" /> {o.destinations || '-'}</div>
                                    <div className="flex items-center gap-2 text-zinc-300"><Clock className="w-4 h-4 text-zinc-500" /> Depart : {fmtDt(o.date_depart)}</div>
                                    <div className="flex items-center gap-2 text-zinc-300"><Clock className="w-4 h-4 text-zinc-500" /> Retour : {fmtDt(o.date_retour_prevu)}</div>
                                </div>

                                {o.autorite && <p className="text-xs text-zinc-500">Autorite : {o.autorite}</p>}
                                {o.observations && <p className="text-sm text-zinc-400 border-t border-zinc-800 pt-2">{o.observations}</p>}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {canManageOrdresMission && (
                <Dialog open={showForm} onOpenChange={(open) => { if (!open) { setShowForm(false); setEditing(null); setForm(emptyForm); } }}>
                    <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[85vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="text-white">{editing ? `Modifier l'ordre ${editing.numero}` : 'Nouvel ordre de mission'}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div>
                                <label className="text-sm text-zinc-400">Objet de la mission *</label>
                                <Input value={form.objet} onChange={e => setForm(f => ({ ...f, objet: e.target.value }))}
                                    className="bg-zinc-800 border-zinc-700 text-white" placeholder="Ex: Transport de materiel vers le centre CARE Nord" data-testid="ordre-objet-input" />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm text-zinc-400">Chauffeur assigne</label>
                                    <Select value={form.chauffeur_id} onValueChange={v => {
                                        const c = chauffeurs.find(x => x.id === v);
                                        setForm(f => ({ ...f, chauffeur_id: v, chauffeur_name: c?.name || '' }));
                                    }}>
                                        <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="ordre-chauffeur-select">
                                            <SelectValue placeholder="Selectionner un chauffeur" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-zinc-900 border-zinc-800">
                                            {chauffeurs.length === 0
                                                ? <div className="px-3 py-2 text-xs text-zinc-500">Aucun chauffeur PL</div>
                                                : chauffeurs.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <label className="text-sm text-zinc-400">Vehicule</label>
                                    <Select value={form.vehicle_id} onValueChange={v => {
                                        const veh = vehicles.find(x => x.id === v);
                                        setForm(f => ({ ...f, vehicle_id: v, vehicle_name: veh?.name || '' }));
                                    }}>
                                        <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="ordre-vehicle-select">
                                            <SelectValue placeholder="Selectionner un vehicule" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-zinc-900 border-zinc-800">
                                            {vehicles.length === 0
                                                ? <div className="px-3 py-2 text-xs text-zinc-500">Aucun vehicule</div>
                                                : vehicles.map(veh => <SelectItem key={veh.id} value={veh.id}>{veh.name} ({veh.plate_number})</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm text-zinc-400">Point de depart</label>
                                    <Input value={form.point_depart} onChange={e => setForm(f => ({ ...f, point_depart: e.target.value }))}
                                        className="bg-zinc-800 border-zinc-700 text-white" placeholder="Ex: Centre technique municipal" data-testid="ordre-depart-input" />
                                </div>
                                <div>
                                    <label className="text-sm text-zinc-400">Destination(s)</label>
                                    <Input value={form.destinations} onChange={e => setForm(f => ({ ...f, destinations: e.target.value }))}
                                        className="bg-zinc-800 border-zinc-700 text-white" placeholder="Ex: Gymnase Jean Jaures" data-testid="ordre-destinations-input" />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm text-zinc-400">Date/heure de depart</label>
                                    <Input type="datetime-local" value={form.date_depart} onChange={e => setForm(f => ({ ...f, date_depart: e.target.value }))}
                                        className="bg-zinc-800 border-zinc-700 text-white" data-testid="ordre-date-depart-input" />
                                </div>
                                <div>
                                    <label className="text-sm text-zinc-400">Date/heure de retour prevu</label>
                                    <Input type="datetime-local" value={form.date_retour_prevu} onChange={e => setForm(f => ({ ...f, date_retour_prevu: e.target.value }))}
                                        className="bg-zinc-800 border-zinc-700 text-white" data-testid="ordre-date-retour-input" />
                                </div>
                            </div>
                            <div>
                                <label className="text-sm text-zinc-400">Autorite qui missionne (signataire)</label>
                                <Input value={form.autorite} onChange={e => setForm(f => ({ ...f, autorite: e.target.value }))}
                                    className="bg-zinc-800 border-zinc-700 text-white" placeholder="Ex: M. le Maire / Directeur des Services Techniques" data-testid="ordre-autorite-input" />
                            </div>
                            <div>
                                <label className="text-sm text-zinc-400">Observations</label>
                                <Textarea value={form.observations} onChange={e => setForm(f => ({ ...f, observations: e.target.value }))}
                                    className="bg-zinc-800 border-zinc-700 text-white" placeholder="Consignes particulieres, itineraire, contact sur place..." data-testid="ordre-observations-input" />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="ghost" onClick={() => { setShowForm(false); setEditing(null); setForm(emptyForm); }}>Annuler</Button>
                            <Button onClick={handleSubmit} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="confirm-ordre-btn">
                                {editing ? 'Enregistrer' : 'Emettre l\'ordre'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
};

export default OrdresMissionPage;
